import { ChevronLeft, ChevronRight } from "lucide-react";

import { SETTINGS_ITEM, isItemActive } from "@/lib/nav-config";
import { Avatar } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { SidebarItem } from "@/components/layout/SidebarItem";
import { cn } from "@/lib/utils";
import { useVisibleNavGroups } from "@/hooks/useNavRoutes";
import { useAuthStore } from "@/stores/auth";
import { useRouteStore } from "@/stores/route";
import { useUIStore } from "@/stores/ui";

/**
 * Persistent left rail navigation. Always visible ≥ 768px desktop.
 *
 * Collapsed (default): 60px, icon only, label as `title` tooltip.
 * Expanded: 240px, icon + label + group headers.
 *
 * Role-aware: items với requiredRole sẽ ẩn nếu user không có role.
 *
 * Doc 28 §4 + Doc 29 §6.
 */
export function PrimarySidebar(): JSX.Element {
  const navigate = useRouteStore((s) => s.navigate);
  const currentRoute = useRouteStore((s) => s.current);
  const collapsed = useUIStore((s) => s.primarySidebarCollapsed);
  const togglePrimarySidebar = useUIStore((s) => s.togglePrimarySidebar);
  const groups = useVisibleNavGroups();
  const user = useAuthStore((s) => s.user);

  const settingsActive = isItemActive(SETTINGS_ITEM, currentRoute);

  return (
    <aside
      data-testid="primary-sidebar"
      className={cn(
        // P2.1-S2: glassmorphism — frosted bg, brand gradient mesh subtle
        "hidden md:flex md:flex-col border-r bg-background/80 backdrop-blur-xl bg-gradient-mesh min-h-0 transition-[width] duration-200",
        collapsed ? "w-[60px]" : "w-[240px]",
      )}
    >
      {/* Header — logo (gradient text) + collapse toggle */}
      <div
        className={cn(
          "h-14 flex items-center border-b shrink-0",
          collapsed ? "justify-center" : "justify-between px-3",
        )}
      >
        {!collapsed ? (
          <div className="flex items-center gap-2 min-w-0">
            <span
              className="size-7 shrink-0 rounded-lg bg-gradient-brand shadow-glow"
              aria-hidden
            />
            <span className="font-bold text-sm truncate text-gradient">
              Trợ lý AI
            </span>
          </div>
        ) : (
          <span
            className="size-7 rounded-lg bg-gradient-brand shadow-glow"
            aria-label="Trợ lý AI"
          />
        )}
        {!collapsed ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 shrink-0"
            onClick={togglePrimarySidebar}
            aria-label="Collapse sidebar"
          >
            <ChevronLeft className="size-4" />
          </Button>
        ) : null}
      </div>

      {/* Body — nav groups */}
      <nav
        className={cn(
          "flex-1 overflow-y-auto py-3",
          collapsed ? "px-1.5 space-y-1.5" : "px-2 space-y-4",
        )}
        aria-label="Primary"
      >
        {groups.map((group) => (
          <div key={group.label} className={collapsed ? "" : "space-y-0.5"}>
            {!collapsed ? (
              <h3 className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground px-3 mb-1">
                {group.label}
              </h3>
            ) : null}
            {group.items.map((item) => (
              <SidebarItem
                key={item.route}
                icon={item.icon}
                label={item.label}
                active={isItemActive(item, currentRoute)}
                collapsed={collapsed}
                onClick={() => navigate(item.route, item.param)}
              />
            ))}
            {/* Visual divider between groups when collapsed */}
            {collapsed ? (
              <div className="my-1.5 h-px bg-border/50 mx-2" />
            ) : null}
          </div>
        ))}
      </nav>

      {/* Footer — Settings + Avatar/User + collapse toggle (when collapsed) */}
      <div
        className={cn(
          "border-t shrink-0",
          collapsed ? "p-1.5 space-y-1" : "p-2 space-y-1",
        )}
      >
        <SidebarItem
          icon={SETTINGS_ITEM.icon}
          label={SETTINGS_ITEM.label}
          active={settingsActive}
          collapsed={collapsed}
          onClick={() => navigate(SETTINGS_ITEM.route)}
        />

        {user ? (
          <div
            className={cn(
              "flex items-center gap-2",
              collapsed ? "justify-center py-1" : "px-2 py-1.5 rounded-md hover:bg-muted",
            )}
            title={collapsed ? user.display_name ?? user.email : undefined}
          >
            <Avatar
              size="sm"
              src={user.avatar_url}
              alt={user.display_name ?? user.email}
              fallback={(user.display_name ?? user.email).slice(0, 2).toUpperCase()}
            />
            {!collapsed ? (
              <div className="flex-1 min-w-0 text-xs">
                <div className="font-medium truncate">
                  {user.display_name ?? user.email}
                </div>
                <div className="text-muted-foreground truncate">{user.email}</div>
              </div>
            ) : null}
          </div>
        ) : null}

        {collapsed ? (
          <Button
            variant="ghost"
            size="icon"
            className="size-10 w-full"
            onClick={togglePrimarySidebar}
            aria-label="Expand sidebar"
            title="Mở rộng sidebar"
          >
            <ChevronRight className="size-4" />
          </Button>
        ) : null}
      </div>
    </aside>
  );
}
